/**
 * BroadScanCheckoutButton — Fase 1C (Broad Scan €75 one-time).
 *
 * Calls POST /api/scan/checkout with whatever context (email/intake_id/
 * ref_code) is in scope. The backend determines the price.
 *
 * Behaviour mirrors EnergyControlCheckoutButton.
 */
import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';

export default function BroadScanCheckoutButton({
  enabled,
  email = null,
  intakeId = null,
  refCode = null,
  label = 'Bestel brede woningscan — € 75',
  showDisabled = false,
  className = '',
}) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const onClick = async () => {
    if (loading) return;
    setErrorMsg('');
    setLoading(true);
    try {
      const res = await fetch('/api/scan/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email || null,
          intake_id: intakeId || null,
          ref_code: refCode || null,
        }),
      });
      if (res.status === 503) {
        setErrorMsg('Deze optie is nog niet actief.');
        return;
      }
      if (!res.ok) {
        setErrorMsg('Checkout kon niet worden gestart. Probeer het later opnieuw.');
        return;
      }
      const data = await res.json();
      if (data?.skipped) {
        setErrorMsg(data?.message || 'Scan is al beschikbaar in uw abonnement.');
        return;
      }
      if (!data?.url) {
        setErrorMsg('Checkout-URL ontbreekt. Probeer het later opnieuw.');
        return;
      }
      if (typeof window !== 'undefined' && window.pwTrack) {
        window.pwTrack('broad_scan_checkout_start', { amount: data.amount });
      }
      window.location.href = data.url;
    } catch (err) {
      setErrorMsg('Netwerkfout. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) {
    if (!showDisabled) return null;
    return (
      <button
        type="button"
        disabled
        className={[
          'inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold',
          'border-2 border-muted bg-muted/30 text-muted-foreground cursor-not-allowed opacity-70',
          className,
        ].join(' ')}
        data-testid="broad-scan-checkout-button-disabled"
        data-fase1-flag="BROAD_SCAN_CHECKOUT_ENABLED"
      >
        <Search className="w-3.5 h-3.5" />
        Binnenkort beschikbaar
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid="broad-scan-checkout-button-wrap">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={[
          'inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold',
          'border-2 border-primary text-primary bg-white hover:bg-primary/5 disabled:opacity-60',
          className,
        ].join(' ')}
        data-testid="broad-scan-checkout-button"
        data-fase1-flag="BROAD_SCAN_CHECKOUT_ENABLED"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
        {loading ? 'Bezig…' : label}
      </button>
      {errorMsg && (
        <p
          className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5"
          data-testid="broad-scan-checkout-error"
        >
          {errorMsg}
        </p>
      )}
    </div>
  );
}
